import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import {
  ESTableWrapper,
  ESTableFilters,
  useESTableBasic,
  ESTableBasicConfig,
  ESMenuItem,
  useElementScrollRestoration,
  ESVirtualizedTable,
} from '@energy-stacks/core/ui';
import {
  NoTableData,
  TableSearchField,
  ESTooltip,
  TableColumnFilter,
  ClearFiltersButton,
  TableColumnSelect,
  TableDateRangeFilter,
  MoreOptionsMenu,
  TableRowsCount,
} from '@energy-stacks/shared';
import { Box, Stack, Typography } from '@mui/material';
import { vehicleStatusFilterOptions } from '@energy-stacks/fleet-is/feature-vehicles-data';
import { VehicleStatusChip } from './VehicleStatusChip';
import { DateRange } from '@energy-stacks/core/date-range-picker';
import { useLocation, useNavigate } from 'react-router-dom';
import { VehicleLogo } from '@energy-stacks/fleet/feature-vehicles';
import { VehicleModel } from '@energy-stacks/fleet-is/feature-vehicles-data';
import { SocChip } from './SocChip';
import { VehicleUpcomingTour } from './VehicleUpcomingTour';

interface VehiclesTableProps extends ESTableBasicConfig<VehicleModel> {
  vehicles: VehicleModel[];
  enableColumnSelection?: boolean;
  enableTimeRangeFilter?: boolean;
  onDateRangeChange?: (dateRange: DateRange) => void;
  defaultDateRange: DateRange;
  dateRange: DateRange;
  right?: React.ReactNode;
  scrollRestorationId?: string;
}

const ROW_HEIGHT = 68.5;

export const VehiclesTable: React.FC<VehiclesTableProps> = ({
  tableId = 'vehicles',
  vehicles,
  defaultRowSelection,
  enableColumnSelection,
  enableTimeRangeFilter,
  enableRowSelection,
  onSelectionChange,
  onDateRangeChange,
  dateRange,
  defaultDateRange,
  right,
  scrollRestorationId,
}) => {
  const [t] = useTranslation('vehicles');
  const [tShared] = useTranslation('shared');
  const [tFleetShared] = useTranslation('fleetShared');
  const navigate = useNavigate();
  const location = useLocation();
  const columnHelper = createColumnHelper<VehicleModel>();

  const columns = [
    columnHelper.accessor('brandAndModel', {
      header: () => t('brandAndModel'),
      footer: (props) => props.column.id,
      cell: ({ cell, row }) => (
        <Stack direction="row" gap={3} alignItems="center">
          <VehicleLogo brandId={row.original.brandId} />
          <Typography variant="inherit">{cell.getValue()}</Typography>
        </Stack>
      ),
      enableGlobalFilter: true,
      size: 140,
    }),
    columnHelper.accessor('name', {
      header: () => t('name'),
      footer: (props) => props.column.id,
      cell: (info) => (
        <Typography variant="inherit">
          {info.getValue().toUpperCase()}
        </Typography>
      ),
      enableGlobalFilter: true,
      size: 120,
    }),
    columnHelper.accessor('licencePlate', {
      header: () => t('licencePlate'),
      footer: (props) => props.column.id,
      cell: (info) => (
        <Typography variant="inherit">
          {info.getValue().toUpperCase()}
        </Typography>
      ),
      enableGlobalFilter: true,
      size: 140,
    }),
    columnHelper.accessor((row) => `${row.weight} ${row.volume}`, {
      id: 'weight',
      header: () => t('capacity'),
      footer: (props) => props.column.id,
      size: 120,
      cell: ({ row }) => {
        const weight = row.original.weight;
        const volume = row.original.volume;
        return (
          <Stack direction="column">
            {weight && (
              <Typography variant="inherit">
                {weight.toLocaleString()} KG
              </Typography>
            )}
            {volume && (
              <Typography variant="inherit">
                {volume.toLocaleString()} m&sup3;
              </Typography>
            )}
          </Stack>
        );
      },
      enableGlobalFilter: false,
    }),
    columnHelper.accessor('status', {
      header: () => t('status'),
      footer: (props) => props.column.id,
      cell: (info) => <VehicleStatusChip status={info.getValue()} />,
      enableGlobalFilter: false,
      enableSorting: false,
      filterFn: 'arrIncludesSome',
      size: 120,
    }),
    columnHelper.accessor('electricVehicleData.soc', {
      header: () => t('currentSoc'),
      footer: (props) => props.column.id,
      cell: (info) => <SocChip percentage={info.getValue()} />,
      sortingFn: 'alphanumeric',
      enableGlobalFilter: false,
    }),
    columnHelper.accessor('tour', {
      header: () => t('tourId'),
      footer: (props) => props.column.id,
      cell: (info) => {
        const tour = info.getValue();
        return tour ? (
          <VehicleUpcomingTour
            tourId={tour.tourId}
            startDate={tour.startDate}
            status={tour.status}
          />
        ) : null;
      },
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'actions',
      size: 20,
      meta: { isActionable: true },
      header: () => tFleetShared('actionsColumnLabel'),
      cell: (info) => {
        const vehicleId = info.row.original.vehicleId;
        return (
          <MoreOptionsMenu testId={vehicleId}>
            <ESMenuItem
              testId={`${vehicleId}EditMenuItem`}
              onClick={() =>
                navigate(`${vehicleId}/edit`, {
                  state: { background: location },
                })
              }
            >
              {tFleetShared('editMoreOptionsMenuItem')}
            </ESMenuItem>
            <ESMenuItem
              testId={`${vehicleId}DeleteMenuItem`}
              onClick={() =>
                navigate(`${vehicleId}/delete`, {
                  state: { background: location },
                })
              }
            >
              {tFleetShared('deleteMoreOptionsMenuItem')}
            </ESMenuItem>
          </MoreOptionsMenu>
        );
      },
    }),
  ];

  const {
    instance,
    rows,
    globalFilter,
    onGlobalFilterChange,
    handleSelectRow,
  } = useESTableBasic(vehicles, columns, {
    tableId,
    fitRowHeight: ROW_HEIGHT,
    defaultRowSelection,
    enableRowSelection,
    onSelectionChange,
    getRowId: (row) => row.vehicleId,
    manualPagination: true,
  });

  const scrollCache = useElementScrollRestoration(
    scrollRestorationId
      ? {
          id: scrollRestorationId,
        }
      : undefined
  );

  return (
    <>
      <ESTableFilters>
        <ESTooltip title={t('searchVehiclesPlaceholder')}>
          <TableSearchField
            placeholder={t('searchVehiclesPlaceholder')}
            value={globalFilter}
            onChange={onGlobalFilterChange}
            tableInstance={instance}
          />
        </ESTooltip>
        {enableTimeRangeFilter && (
          <TableDateRangeFilter
            title={tShared('timeRange')}
            isActive
            defaultDateRange={defaultDateRange}
            dateRange={dateRange}
            onDateRangeApplied={(dateRange) => {
              onDateRangeChange?.(dateRange);
            }}
            onDateRangeCleared={() => {
              onDateRangeChange?.(defaultDateRange);
            }}
          />
        )}
        <TableColumnFilter
          column={instance.getColumn('status')}
          columnLabel={t('status')}
          options={vehicleStatusFilterOptions.map((v) => ({
            label: t(`vehicleStatuses.${v}`),
            value: v,
          }))}
          isClearable
        />
        <ClearFiltersButton tableInstance={instance} />
        {enableColumnSelection ? (
          <Box sx={{ marginLeft: 'auto' }}>
            <TableColumnSelect instance={instance} />
          </Box>
        ) : null}
        {right ? right : null}
      </ESTableFilters>
      <ESTableWrapper>
        <ESVirtualizedTable
          instance={instance}
          rows={rows}
          rowHeight={ROW_HEIGHT}
          onRowClick={handleSelectRow}
          scrollRestorationId={scrollRestorationId}
          initialScrollTop={scrollCache.scrollY}
        />
        {rows.length === 0 ? (
          <NoTableData message={t('thereAreNoVehicles')} />
        ) : null}
        <TableRowsCount
          instance={instance}
          totalCountLabel={t('totalRowsCount')}
        />
      </ESTableWrapper>
    </>
  );
};
